import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useRentmanCustomers } from '../hooks/useRentmanCustomers';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { useVirtualizer } from '@tanstack/react-virtual';

// Memoized customer item component
const CustomerCommandItem = React.memo(({ customer, selectedCustomer, onSelect, style }) => (
  <div style={style}>
    <CommandItem
      value={customer.displayname || customer.name}
      onSelect={() => onSelect(customer)}
      className="cursor-pointer"
    >
      {customer.displayname || customer.name}
      <Check
        className={cn(
          "ml-auto h-4 w-4",
          selectedCustomer?.id === customer.id ? "opacity-100" : "opacity-0"
        )}
      />
    </CommandItem>
  </div>
));

CustomerCommandItem.displayName = 'CustomerCommandItem';

export function CustomerSelect({ onSelect, selectedCustomer, className }) {
  const { customers, loading, error } = useRentmanCustomers();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef(null);
  const [listKey, setListKey] = useState(0);
  const [useCustomCustomer, setUseCustomCustomer] = useState(false);
  const [customCustomerName, setCustomCustomerName] = useState("");

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setListKey(prev => prev + 1);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [open]);

  // Memoize filtered customers
  const filteredCustomers = useMemo(() => {
    if (!customers?.length) return [];
    const query = searchQuery.toLowerCase().trim();
    
    const filtered = query === "" 
      ? customers 
      : customers.filter(customer => {
          const searchableText = (customer.displayname || customer.name || "").toLowerCase();
          return searchableText.includes(query);
        });
    
    return filtered;
  }, [customers, searchQuery]);

  // Memoize the selection handler
  const handleSelect = useCallback((customer) => {
    onSelect(customer);
    setOpen(false);
  }, [onSelect]);

  // Handle custom customer selection
  useEffect(() => {
    if (useCustomCustomer) {
      if (customCustomerName.trim()) {
        onSelect({
          id: 'custom',
          name: customCustomerName.trim(),
          displayname: customCustomerName.trim(),
          isCustom: true,
        });
      } else {
        onSelect(null);
      }
    }
  }, [useCustomCustomer, customCustomerName, onSelect]);

  // Reset custom name if toggling back to dropdown
  useEffect(() => {
    if (!useCustomCustomer) {
      setCustomCustomerName("");
    }
  }, [useCustomCustomer]);

  const parentRef = React.useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: filteredCustomers.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 5,
  });

  // Calculate dynamic height based on number of items
  const dynamicHeight = Math.min(filteredCustomers.length * 36, 250); // 36px per item, max 250px

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive p-4">
        Error loading customers: {error}
      </div>
    );
  }

  return (
    <div className={cn("w-full space-y-2", className)}>
      <div>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={useCustomCustomer}
            onChange={e => setUseCustomCustomer(e.target.checked)}
            className="accent-primary"
          />
          Customer not found? Enter manually
        </label>
      </div>
      {useCustomCustomer ? (
        <input
          type="text"
          className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-primary bg-background text-foreground placeholder:text-muted-foreground"
          placeholder="Enter customer name..."
          value={customCustomerName}
          onChange={e => setCustomCustomerName(e.target.value)}
          required
        />
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
            >
              {selectedCustomer && !selectedCustomer.isCustom
                ? selectedCustomer.displayname || selectedCustomer.name
                : "Select customer..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" side="bottom">
            <Command shouldFilter={false}>
              <CommandInput 
                ref={inputRef}
                placeholder="Search customers..." 
                className="h-9"
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList key={listKey} className="max-h-[300px]">
                {filteredCustomers.length === 0 ? (
                  <CommandEmpty>No customers found.</CommandEmpty>
                ) : (
                  <CommandGroup className="overflow-hidden">
                    <div
                      ref={parentRef}
                      className="relative overflow-auto"
                      style={{ height: `${dynamicHeight}px` }}
                    >
                      <div
                        style={{
                          height: `${rowVirtualizer.getTotalSize()}px`,
                          width: '100%',
                          position: 'relative',
                        }}
                      >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                          const customer = filteredCustomers[virtualRow.index];
                          return (
                            <CustomerCommandItem
                              key={customer.id}
                              customer={customer}
                              selectedCustomer={selectedCustomer}
                              onSelect={handleSelect}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualRow.start}px)`,
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
} 